import React from 'react';
import { Pressable } from 'react-native';

import { Avatar, HStack, Text, VStack, theme } from '@squire/ui';

import { HorizontalSeparator } from '@/components/_shared/HorizontalSeparator/HorizontalSeparator';
import { TipSelector } from '@/components/booking/TipSelector';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import {
  formatCurrency,
  formatCurrencyOptionalCents,
} from '@/utils/currencyUtils';
import {
  DateFormat,
  formatDate,
  formatDateWithTimezone,
} from '@/utils/dateUtils';

import { styles } from './styles';
import type { ViewWaitlistCardProps } from './types';

const ViewWaitlistCard: React.FC<ViewWaitlistCardProps> = ({
  barber,
  services,
  day,
  tipAmount,
  serviceDuration,
  shop,
  uiState,
  selectedTime,
  onChooseTime,
  onChooseDifferentTime,
  availableTips,
  canAddTip,
  onSelectTip,
  tipLoading,
}) => {
  const { t } = useScreenTranslations('ViewWaitlistCard');

  const professionalName = barber
    ? `${barber.firstName} ${barber.lastName ? barber.lastName.charAt(0) + '.' : ''}`
    : '';
  const professionalPhotoUrl = barber?.photos?.[0]?.url;

  const mainService = services[0];
  const addOnServices = services.slice(1);

  // Calculate totals
  const servicesTotal = services.reduce(
    (sum, service) => sum + service.cost,
    0,
  );
  const itemTotal = servicesTotal + (tipAmount || 0);

  // Use formatDate (not formatDateWithTimezone) because `day` is a calendar date
  // (e.g. "2026-03-20T00:00:00.000Z"), not a point in time. Timezone-shifting
  // midnight UTC would roll it back to the previous day in western timezones.
  const formattedDate = formatDate(
    day.split('T')[0],
    DateFormat.CART_ITEM_DATE,
  );

  const renderDateTimeRow = () => {
    if (uiState === 'active') {
      return (
        <HStack style={styles.spaceBetween}>
          <Text variant="l" color="textSecondary">
            {t('date')}
          </Text>
          <Text variant="l" color="textSecondary">
            {formattedDate}
          </Text>
        </HStack>
      );
    }

    if (uiState === 'available') {
      return (
        <HStack style={styles.spaceBetween}>
          <Text variant="l" color="textSecondary">
            {t('time')}
          </Text>
          <Pressable onPress={onChooseTime}>
            <Text variant="l" color="textBrand">
              {t('chooseTime')}
            </Text>
          </Pressable>
        </HStack>
      );
    }

    if (uiState === 'expired') {
      return (
        <HStack style={styles.spaceBetween}>
          <Text variant="l" color="textSecondary">
            {t('time')}
          </Text>
          <Pressable onPress={onChooseDifferentTime}>
            <Text variant="l" color="textBrand">
              {t('chooseDifferentTime')}
            </Text>
          </Pressable>
        </HStack>
      );
    }

    if (uiState === 'selected') {
      const formattedTime = selectedTime
        ? formatDateWithTimezone(
            selectedTime,
            DateFormat.CART_ITEM_TIME,
            shop.timezone,
          )
        : '';

      return (
        <HStack style={styles.spaceBetween}>
          <Text variant="l" color="textSecondary">
            {t('time')}
          </Text>
          <Pressable onPress={onChooseTime}>
            <Text variant="l" color="textBrand">
              {formattedTime}
            </Text>
          </Pressable>
        </HStack>
      );
    }

    return null;
  };

  const showStaticTipRow = uiState === 'active' || uiState === 'expired';
  const showTipSelector =
    (uiState === 'available' || uiState === 'selected') &&
    canAddTip &&
    onSelectTip &&
    availableTips;

  return (
    <VStack space={theme.spacing[4]}>
      {/* Barber & Service Section */}
      <HStack space={theme.spacing[4]} style={styles.rowStart}>
        <Avatar
          uri={professionalPhotoUrl}
          size="md"
          shape="square"
          fallbackText={professionalName}
        />
        <VStack flex space={2}>
          <HStack style={styles.spaceBetween}>
            <Text variant="xlStrong" color="textPrimary">
              {professionalName}
            </Text>
            <Text variant="xlStrong" color="textPrimary">
              {formatCurrencyOptionalCents(servicesTotal, shop.currency)}
            </Text>
          </HStack>
          <Text variant="l" color="textSecondary">
            {mainService?.name || ''}
          </Text>
        </VStack>
      </HStack>

      {/* Additional Services */}
      {addOnServices.map((service) => (
        <HStack key={service.id} style={styles.spaceBetween}>
          <Text variant="l" color="textPrimary">
            {service.name}
          </Text>
          <Text variant="l" color="textSecondary">
            {formatCurrency(service.cost, shop.currency)}
          </Text>
        </HStack>
      ))}

      {/* Duration */}
      <HStack style={styles.spaceBetween}>
        <Text variant="l" color="textSecondary">
          {t('duration')}
        </Text>
        <Text variant="l" color="textSecondary">
          {t('durationValue', { minutes: serviceDuration })}
        </Text>
      </HStack>

      {/* Date/Time */}
      {renderDateTimeRow()}

      {/* Tip - static for active state */}
      {showStaticTipRow && (
        <HStack style={styles.spaceBetween}>
          <Text variant="l" color="textSecondary">
            {t('tip')}
          </Text>
          <Text variant="l" color="textSecondary">
            {formatCurrencyOptionalCents(tipAmount || 0, shop.currency)}
          </Text>
        </HStack>
      )}

      {/* Tip - always show amount row when selector is available */}
      {showTipSelector && (
        <HStack style={styles.spaceBetween}>
          <Text variant="l" color="textSecondary">
            {t('tip')}
          </Text>
          <Text variant="l" color="textSecondary">
            {formatCurrencyOptionalCents(tipAmount || 0, shop.currency)}
          </Text>
        </HStack>
      )}

      {/* Interactive Tip Selector */}
      {showTipSelector && (
        <TipSelector
          tips={availableTips}
          selectedTipAmount={tipAmount}
          onSelectTip={onSelectTip}
          loading={tipLoading}
          inverted
          currency={shop.currency}
        />
      )}

      <HorizontalSeparator style={styles.separator} />

      {/* Total Price */}
      <VStack space={theme.spacing[3]}>
        <HStack style={styles.spaceBetween}>
          <Text variant="xlStrong" color="textPrimary">
            {t('total')}
          </Text>
          <Text variant="xlStrong" color="textPrimary">
            {formatCurrencyOptionalCents(itemTotal, shop.currency)}
          </Text>
        </HStack>
      </VStack>
    </VStack>
  );
};

ViewWaitlistCard.displayName = 'ViewWaitlistCard';
export { ViewWaitlistCard };
